import { createFileRoute, Link } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { CTABlock } from "@/components/CTABlock";
import { StaggerContainer, StaggerItem } from "@/components/AnimatedSection";
import { listPublicGalleryPhotos, type GalleryPhoto } from "@/lib/gallery.functions";
import { seoHead } from "@/lib/seo";

const MARQUES = [
  { id: "All", label: "All marques", match: () => true },
  { id: "Rolls-Royce", label: "Rolls-Royce", match: (s: string) => /rolls|ghost|phantom|cullinan|wraith/.test(s) },
  { id: "Bentley", label: "Bentley", match: (s: string) => /bentley|continental|flying spur|bentayga/.test(s) },
  { id: "McLaren", label: "McLaren", match: (s: string) => /mclaren/.test(s) },
  { id: "AMG / Mercedes", label: "AMG / Mercedes", match: (s: string) => /amg|mercedes|s-?class|e63|c63|g-?wagen/.test(s) },
  { id: "Porsche", label: "Porsche", match: (s: string) => /porsche|911|panamera|cayenne|taycan|macan/.test(s) },
  { id: "Audi / RS", label: "Audi / RS", match: (s: string) => /audi|\brs\d|\bs[3-8]\b/.test(s) },
  { id: "BMW / M", label: "BMW / M", match: (s: string) => /bmw|\bm[3-8]\b|x[3-7]/.test(s) },
] as const;

const searchSchema = z.object({
  marque: fallback(z.string(), "All").default("All"),
});
type Search = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/premium-gallery")({
  validateSearch: zodValidator(searchSchema),
  loader: async () => {
    const r = await listPublicGalleryPhotos({ data: { limit: 60 } }).catch(() => ({ photos: [] as GalleryPhoto[] }));
    return { photos: r.photos };
  },
  head: () =>
    seoHead({
      path: "/premium-gallery",
      title: "Prestige & Performance Workshop Gallery — Rolls-Royce, Bentley, McLaren, AMG | Woodseer Autos E14",
      description:
        "Photos of prestige and performance cars cared for at Woodseer Autos in Poplar, East London — Rolls-Royce, Bentley, McLaren, AMG, Porsche, RS and M cars. Independent garage, dealer-standard work.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Prestige & Performance", path: "/premium-gallery" },
      ],
    }),
  component: PremiumGalleryPage,
});

function PremiumGalleryPage() {
  const { photos } = Route.useLoaderData();
  const { marque } = Route.useSearch();

  const selected = MARQUES.find((m) => m.id === marque) ?? MARQUES[0];
  const filtered = photos.filter((p: GalleryPhoto) => {
    const hay = `${p.alt_text ?? ""} ${p.caption ?? ""} ${p.category ?? ""}`.toLowerCase();
    return selected.match(hay);
  });

  return (
    <>
      <section className="section-y bg-foreground text-background border-b-2 border-primary">
        <div className="mx-auto max-w-5xl px-4 md:px-6 text-center">
          <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-primary">Prestige &amp; performance</p>
          <h1 className="mt-4 text-4xl md:text-5xl font-extrabold tracking-tight">
            The cars we look after — under one roof in E14.
          </h1>
          <p className="mt-5 text-background/80 leading-relaxed max-w-2xl mx-auto">
            From everyday runabouts to McLarens and Rolls-Royces, we treat every car with the same care.
            Browse by marque to see real photos from our Poplar workshop.
          </p>
        </div>
      </section>

      <section className="section-y">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {MARQUES.map((m) => (
              <Link
                key={m.id}
                from="/premium-gallery"
                search={(prev: Search) => ({ ...prev, marque: m.id })}
                className={`px-4 py-2 rounded-lg text-[12.5px] font-bold tracking-tight transition-colors ${
                  marque === m.id
                    ? "bg-primary text-primary-foreground shadow-md shadow-primary/25"
                    : "bg-card border border-border text-foreground/75 hover:text-foreground hover:border-primary/30"
                }`}
              >
                {m.label}
              </Link>
            ))}
          </div>

          {filtered.length === 0 ? (
            <p className="text-center text-sm text-muted-foreground py-12">
              No photos for that marque yet — try another, or{" "}
              <Link from="/premium-gallery" search={{ marque: "All" }} className="text-primary font-bold underline">
                see all
              </Link>
              .
            </p>
          ) : (
            <StaggerContainer className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((item: GalleryPhoto, i: number) => (
                <StaggerItem key={`${item.id}-${i}`}>
                  <figure className="group relative overflow-hidden rounded-2xl border border-border aspect-[3/2] shadow-lg shadow-black/[0.06]">
                    <img
                      src={item.variants?.medium?.url || item.public_url}
                      alt={item.alt_text}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-5">
                      <figcaption className="text-sm text-white font-semibold leading-snug">
                        {item.caption || item.alt_text}
                      </figcaption>
                    </div>
                  </figure>
                </StaggerItem>
              ))}
            </StaggerContainer>
          )}
        </div>
      </section>

      <CTABlock
        headline="Prestige car needs work?"
        subtext="Call us to discuss. Independent pricing, dealer-standard work — and a straight answer before anything starts."
      />
    </>
  );
}
